import * as THREE from 'three';
import { Quadtree, Boundary } from './SpatialPartition';
import { GameObject, ObjectType } from '../objects/GameObject';
import { Config } from '../core/Config';
import { DynamicObject, UpdatePriority, MovementState } from '../objects/dynamic/DynamicObject';
import { FrustumCullingSystem } from './FrustumCullingSystem';

// Define performance metrics interface
interface PerformanceMetrics {
  totalUpdates: number;
  directUpdates: number;
  batchUpdates: number;
  rebuildTime: number;
  lastUpdateTime: number;
}

export class SpatialManager {
  private quadtree: Quadtree;
  private scene: THREE.Scene;
  private showDebug: boolean = false;
  private objects: Map<string, GameObject> = new Map();
  
  // Performance optimization for dynamic objects
  private dynamicObjects: Set<DynamicObject> = new Set();
  private staticObjects: Set<GameObject> = new Set();
  private dirtyDynamicObjects: Set<DynamicObject> = new Set();
  
  // Performance tracking
  private lastRebuildTime: number = 0;
  private rebuildInterval: number = Config.DYNAMIC_OBJECT_SETTINGS.UPDATE_INTERVAL_MS; // ms
  private movementThreshold: number = Config.DYNAMIC_OBJECT_SETTINGS.QUADTREE_UPDATE_THRESHOLD; // min distance to trigger quadtree update
  private batchUpdateSize: number = Config.DYNAMIC_OBJECT_SETTINGS.BATCH_UPDATE_SIZE; // max number of dynamic objects to update per frame
  private updateCounter: number = 0;
  private priorityAdjustCounter: number = 0;
  
  // Performance metrics
  private performanceMetrics: PerformanceMetrics = {
    totalUpdates: 0,
    directUpdates: 0,
    batchUpdates: 0,
    rebuildTime: 0,
    lastUpdateTime: 0
  };
  
  // Frustum culling reference for visibility checks
  private frustumCulling: FrustumCullingSystem | null = null;

  // Shared vectors to reduce garbage collection
  private static readonly tempVec3: THREE.Vector3 = new THREE.Vector3();

  // Added for adjustDynamicObjectPriorities method
  private lastCheckedIndex: number = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    
    // Create the root quadtree - size should be based on your game world
    const worldSize = Config.TERRAIN_SIZE;
    const boundary = new Boundary(0, 0, worldSize / 2, worldSize / 2);
    this.quadtree = new Quadtree(boundary, Config.QUADTREE_CAPACITY);
    
    console.log('SpatialManager initialized with world size:', worldSize);
    
    // Initialize last rebuild time
    this.lastRebuildTime = performance.now();
  }

  // Add an object to the spatial manager
  public addObject(object: GameObject): void {
    // Store object in our map
    this.objects.set(object.getId(), object);
    
    // Sort into static or dynamic collections for optimization
    if (object.getType() === ObjectType.STATIC) {
      this.staticObjects.add(object);
    } else if (object instanceof DynamicObject) {
      this.dynamicObjects.add(object);
    }
    
    // Insert into quadtree
    this.quadtree.insert(object);
  }

  // Remove an object from the spatial manager
  public removeObject(objectId: string): void {
    if (this.objects.has(objectId)) {
      const object = this.objects.get(objectId)!;
      
      // Remove from appropriate collection
      if (object.getType() === ObjectType.STATIC) {
        this.staticObjects.delete(object);
      } else if (object instanceof DynamicObject) {
        this.dynamicObjects.delete(object as DynamicObject);
        this.dirtyDynamicObjects.delete(object as DynamicObject);
      }
      
      // Remove from objects map
      this.objects.delete(objectId);
      
      // Remove directly from quadtree using efficient removal
      this.quadtree.remove(objectId);
    }
  }

  // Update object position
  public updateObject(object: GameObject): void {
    // For dynamic objects, we'll batch updates for better performance
    if (object instanceof DynamicObject) {
      if (object.isDirtyState()) {
        this.dirtyDynamicObjects.add(object);
        object.resetDirtyFlag(); // Reset dirty flag to avoid duplicate updates
      }
    } else {
      // For static objects, immediate rebuild
      this.scheduleRebuild();
    }
  }
  
  /**
   * Update method to be called each frame for handling deferred spatial updates
   * Implements batched/lazy updating of the quadtree for dynamic objects
   * @param deltaTime Time since last frame
   */
  public update(deltaTime: number): void {
    const currentTime = performance.now();
    
    // Check if we have dirty dynamic objects to update
    if (this.dirtyDynamicObjects.size > 0) {
      // Process a batch of dynamic objects each frame
      this.processDynamicObjectBatch();
    }
    
    // Adjust priorities based on frustum visibility periodically
    this.priorityAdjustCounter++;
    if (this.priorityAdjustCounter >= Config.DYNAMIC_OBJECT_SETTINGS.PRIORITY_ADJUST_INTERVAL) {
      this.priorityAdjustCounter = 0;
      this.adjustDynamicObjectPriorities();
    }
    
    // Check if it's time for a full rebuild
    // Only do this if we have dirty objects or sufficient time has passed
    // AND we haven't just rebuilt in the last few frames (to prevent constant rebuilds)
    const timeSinceLastRebuild = currentTime - this.lastRebuildTime;
    
    // Only rebuild if:
    // 1. We have dirty objects that couldn't be individually updated AND it's been long enough since last rebuild
    // 2. OR it's been a very long time since the last rebuild (5x interval)
    if ((this.dirtyDynamicObjects.size > 0 && 
         timeSinceLastRebuild > this.rebuildInterval && 
         timeSinceLastRebuild < this.rebuildInterval * 2) ||
        (timeSinceLastRebuild > this.rebuildInterval * 5)) {
      
      // Track rebuilds so we don't do too many
      this.rebuildQuadtree();
      this.lastRebuildTime = currentTime;
      this.dirtyDynamicObjects.clear();
    }
    
    // Update debug visualization if enabled - but do this much less frequently
    if (this.showDebug && this.updateCounter++ % 100 === 0) {
      this.quadtree.createDebugMesh(this.scene);
    }
  }
  
  /**
   * Process a batch of dynamic objects for quadtree updates
   * This limits the performance impact by handling only a few per frame
   */
  private processDynamicObjectBatch(): void {
    const startTime = performance.now();
    let processed = 0;
    
    // Use iterator to avoid creating arrays and unnecessary allocations
    const iterator = this.dirtyDynamicObjects.values();
    let result = iterator.next();
    
    while (!result.done && processed < this.batchUpdateSize) {
      const dynObject = result.value;
      
      // Only process if it's actually moved enough to warrant an update
      const previousPos = dynObject.getPreviousPosition 
        ? dynObject.getPreviousPosition() 
        : SpatialManager.tempVec3.set(0, 0, 0);
      
      const currentPos = dynObject.getPosition();
      const distanceMoved = currentPos.distanceTo(previousPos);
      
      // Validate that the object is in a reasonable position
      const worldSize = Config.TERRAIN_SIZE;
      const halfSize = worldSize / 2;
      const marginOfError = 1.0; // Slightly larger than the boundary margin
      
      const needsUpdate = distanceMoved > this.movementThreshold ||
                          Math.abs(currentPos.x) > halfSize - marginOfError ||
                          Math.abs(currentPos.z) > halfSize - marginOfError;
      
      if (needsUpdate) {
        // Ensure position is within world boundaries before updating
        if (Math.abs(currentPos.x) > halfSize || Math.abs(currentPos.z) > halfSize) {
          // Force position to be within bounds before updating
          const fixedPos = new THREE.Vector3(
            Math.max(-halfSize + 0.1, Math.min(halfSize - 0.1, currentPos.x)),
            currentPos.y,
            Math.max(-halfSize + 0.1, Math.min(halfSize - 0.1, currentPos.z))
          );
          dynObject.setPosition(fixedPos.x, fixedPos.y, fixedPos.z);
          console.warn(`Fixed object ${dynObject.getId()} position that was outside world bounds.`);
        }
        
        // For significant movement, do a targeted update
        this.updateSingleObjectInQuadtree(dynObject);
        this.dirtyDynamicObjects.delete(dynObject);
        processed++;
        
        // Update performance metrics
        this.performanceMetrics.directUpdates++;
        this.performanceMetrics.totalUpdates++;
      }
      
      result = iterator.next();
    }
    
    // Update batch metrics
    if (processed > 0) {
      this.performanceMetrics.batchUpdates++;
      this.performanceMetrics.lastUpdateTime = performance.now() - startTime;
    }
  }
  
  /**
   * Update a single object in the quadtree without rebuilding the entire structure
   * More efficient for sparse updates to dynamic objects
   */
  private updateSingleObjectInQuadtree(object: GameObject): void {
    // Use the new direct update method in Quadtree
    // This efficiently removes and reinserts only when necessary
    this.quadtree.update(object);
  }

  // Rebuild the entire quadtree (after object positions change)
  private rebuildQuadtree(): void {
    const startTime = performance.now();
    
    // Only rebuild if we have enough objects to warrant it
    if (this.dynamicObjects.size === 0 && this.staticObjects.size === 0) {
      return;
    }
    
    // Keep track of object counts before rebuilding to detect leaks
    const prevObjectCount = this.quadtree.getStats().objects;
    
    // Clear existing quadtree
    // Clean up debug visualization before clearing the quadtree
    if (this.showDebug) {
      this.cleanupDebugVisualizations();
    }
    
    this.quadtree.clear();
    Quadtree.clearObjectNodeMap();
    
    // Ensure all objects are within world boundaries before inserting
    const worldSize = Config.TERRAIN_SIZE;
    const halfSize = worldSize / 2;
    const margin = 0.1; // Small margin
    
    // Start with static objects as they don't change often
    for (const object of this.staticObjects) {
      this.quadtree.insert(object);
    }
    
    // Then add dynamic objects, fixing any that are outside bounds
    let fixedPositions = 0;
    for (const object of this.dynamicObjects) {
      const pos = object.getPosition();
      
      // Ensure position is within world boundaries before inserting
      if (Math.abs(pos.x) > halfSize - margin || Math.abs(pos.z) > halfSize - margin) {
        // Fix position to be within bounds
        const fixedPos = new THREE.Vector3(
          Math.max(-halfSize + margin, Math.min(halfSize - margin, pos.x)),
          pos.y,
          Math.max(-halfSize + margin, Math.min(halfSize - margin, pos.z))
        );
        object.setPosition(fixedPos.x, fixedPos.y, fixedPos.z);
        fixedPositions++;
      }
      
      this.quadtree.insert(object);
    }
    
    // Log if we had to fix any positions
    if (fixedPositions > 0) {
      console.warn(`Fixed ${fixedPositions} objects that were outside world bounds during quadtree rebuild.`);
    }
    
    // Detect potential object leaks
    const newObjectCount = this.quadtree.getStats().objects;
    if (newObjectCount !== prevObjectCount && 
        newObjectCount !== this.staticObjects.size + this.dynamicObjects.size) {
      console.warn(`Possible object tracking issue: Before rebuild: ${prevObjectCount}, After rebuild: ${newObjectCount}, Expected: ${this.staticObjects.size + this.dynamicObjects.size}`);
    }
    
    // Update performance metrics
    this.performanceMetrics.rebuildTime = performance.now() - startTime;
    this.performanceMetrics.totalUpdates++;
    
    // Re-create debug visualization if enabled
    if (this.showDebug) {
      this.quadtree.createDebugMesh(this.scene);
    }
    
    // Only log if rebuild took significant time
    if (this.performanceMetrics.rebuildTime > 1.0) {
      console.log(`Quadtree rebuilt with ${this.staticObjects.size} static and ${this.dynamicObjects.size} dynamic objects in ${this.performanceMetrics.rebuildTime.toFixed(2)}ms`);
    }
  }
  
  /**
   * Schedule a rebuild of the quadtree instead of doing it immediately
   * This helps batch multiple updates together for better performance
   */
  private scheduleRebuild(): void {
    // We'll do the actual rebuild in the update method
    const currentTime = performance.now();
    
    // If it's been a while since the last rebuild, do it now
    if (currentTime - this.lastRebuildTime > this.rebuildInterval) {
      this.rebuildQuadtree();
      this.lastRebuildTime = currentTime;
    }
  }

  // Get objects within a radius of a point
  public getObjectsInRadius(center: THREE.Vector3, radius: number): GameObject[] {
    return this.quadtree.queryRadius(center, radius);
  }

  // Get objects within a rectangular area
  public getObjectsInArea(x: number, z: number, width: number, height: number): GameObject[] {
    const boundary = new Boundary(x, z, width, height);
    return this.quadtree.query(boundary);
  }

  // Enable/disable debug visualization
  public toggleDebugVisualization(): void {
    this.showDebug = !this.showDebug;
    
    if (this.showDebug) {
      // Before creating new debug visualization, clean up any existing ones
      this.cleanupDebugVisualizations();
      
      // Show quadtree visualization
      this.quadtree.createDebugMesh(this.scene);
      console.log('Debug visualization enabled');
    } else {
      // Clean up debug visualization
      this.cleanupDebugVisualizations();
      console.log('Debug visualization disabled');
    }
  }
  
  /**
   * Clean up all debug visualizations to prevent memory leaks
   */
  public cleanupDebugVisualizations(): void {
    // Clean up quadtree visualizations
    Quadtree.clearAllDebugMeshes(this.scene);
  }
  
  // Get statistics about the quadtree
  public getStats(): { 
    nodes: number, 
    objects: number, 
    maxDepth: number, 
    staticCount: number, 
    dynamicCount: number,
    dirtyCount: number,
    performance: PerformanceMetrics
  } {
    return {
      ...this.quadtree.getStats(),
      staticCount: this.staticObjects.size,
      dynamicCount: this.dynamicObjects.size,
      dirtyCount: this.dirtyDynamicObjects.size,
      performance: { ...this.performanceMetrics }
    };
  }
  
  // Clear all objects and reset
  public clear(): void {
    this.objects.clear();
    this.staticObjects.clear();
    this.dynamicObjects.clear();
    this.dirtyDynamicObjects.clear();
    this.quadtree.clear();
    
    if (this.showDebug) {
      this.quadtree.removeDebugMesh(this.scene);
    }
  }

  /**
   * Set the frustum culling system for visibility checks
   * @param frustumCulling The frustum culling system to use
   */
  public setFrustumCulling(frustumCulling: FrustumCullingSystem): void {
    this.frustumCulling = frustumCulling;
  }
  
  /**
   * Adjust dynamic object priorities based on visibility and distance from camera
   * Off-screen objects get DORMANT or LOW priority
   * Distant objects get lower priority than nearby objects
   */
  private adjustDynamicObjectPriorities(): void {
    // Skip if frustum culling system isn't available
    if (!this.frustumCulling || this.dynamicObjects.size === 0) {
      return;
    }
    
    // Settings for quick access
    const settings = Config.DYNAMIC_OBJECT_SETTINGS;
    
    // Track adjustment stats
    let objectsChecked = 0;
    let objectsAdjusted = 0;
    let objectsDormant = 0;
    let objectsWokenUp = 0;
    
    // Get camera position once for distance calculations
    const cameraPosition = this.frustumCulling.getCamera().getPosition();
    
    // Only check a subset of objects each time to spread the cost
    // This is important for larger scenes to avoid checking all objects every time
    const maxObjectsToCheck = Math.min(20, this.dynamicObjects.size);
    const dynamicObjectsArray = Array.from(this.dynamicObjects);
    
    // Use a rotating index to check different objects each time
    this.lastCheckedIndex = (this.lastCheckedIndex || 0) % this.dynamicObjects.size;
    const startIndex = this.lastCheckedIndex;
    let objectsProcessed = 0;
    
    for (let i = 0; i < maxObjectsToCheck; i++) {
      const index = (startIndex + i) % dynamicObjectsArray.length;
      const object = dynamicObjectsArray[index];
      objectsChecked++;
      objectsProcessed++;
      
      // Ensure the object has the required methods
      if (!object.getUpdatePriority || !object.setUpdatePriority || 
          !object.getMovementState || !object.setDormant) {
        continue;
      }
      
      // Get current state
      const oldPriority = object.getUpdatePriority();
      const oldState = object.getMovementState();
      
      // Check if object is in the frustum and its distance
      const visibility = this.frustumCulling.checkObjectVisibility(object);
      if (!visibility) continue;
      
      let newPriority = oldPriority;
      let shouldBeDormant = false;
      
      // Handle objects outside the view frustum
      if (!visibility.visible && settings.DORMANT_WHEN_OFFSCREEN) {
        // Make off-screen objects dormant or low priority
        // But don't make them dormant immediately - give them time to move out of view
        if (oldState !== MovementState.DORMANT && oldState !== MovementState.IDLE) {
          shouldBeDormant = true;
        }
      } 
      // Handle visible objects based on distance
      else if (visibility.visible && settings.REDUCE_PRIORITY_WHEN_DISTANT) {
        // Close objects get high priority
        if (visibility.distanceSquared < settings.PRIORITY_NEAR_DISTANCE_SQ) {
          newPriority = UpdatePriority.HIGH;
        } 
        // Medium distance objects get medium priority
        else if (visibility.distanceSquared < settings.PRIORITY_MEDIUM_DISTANCE_SQ) {
          newPriority = UpdatePriority.MEDIUM;
        } 
        // Far objects get low priority
        else {
          newPriority = UpdatePriority.LOW;
        }
      }
      
      // Only apply priority changes if significant
      // This reduces unnecessary changes and state churn
      const priorityChanged = newPriority !== oldPriority;
      if (priorityChanged && 
          // Special case: don't downgrade HIGH to MEDIUM too eagerly
          !(oldPriority === UpdatePriority.HIGH && newPriority === UpdatePriority.MEDIUM)) {
        object.setUpdatePriority(newPriority);
        objectsAdjusted++;
      }
      
      // Apply dormant state if needed but be conservative
      if (shouldBeDormant) {
        object.setDormant(true);
        objectsDormant++;
      } 
      // Wake up dormant objects that should be active
      else if (visibility.visible && oldState === MovementState.DORMANT) {
        object.setDormant(false);
        objectsWokenUp++;
        
        // When waking up from dormant state, ensure we update the quadtree
        // but avoid marking too many objects dirty at once
        this.dirtyDynamicObjects.add(object);
      }
    }
    
    // Update index for next time
    this.lastCheckedIndex = (startIndex + objectsProcessed) % this.dynamicObjects.size;
    
    // Log statistics if significant changes occurred but don't spam the console
    if ((objectsAdjusted > 0 || objectsDormant > 0 || objectsWokenUp > 0) && 
        (objectsAdjusted > maxObjectsToCheck/4 || objectsDormant > 2 || objectsWokenUp > 0)) {
      console.log(`Priority adjustment: ${objectsChecked}/${this.dynamicObjects.size} objects checked, ${objectsAdjusted} priorities adjusted, ${objectsDormant} set dormant, ${objectsWokenUp} woken up`);
    }
  }
} 